
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Lead } from '@/types/lead';
import { useLeadScore } from '@/hooks/useLeadScore';
import LeadScoreBreakdown from './LeadScoreBreakdown';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';

interface ScoreTabContentProps {
  lead: Lead;
}

const ScoreTabContent: React.FC<ScoreTabContentProps> = ({ lead }) => {
  const {
    leadScore,
    scoreBreakdown,
    scoreHistory,
    isLoading,
    isCalculating,
    recalculateScore
  } = useLeadScore(lead.id);

  // Create chart data for score components
  const scoreComponents = [
    { name: 'Demographic', value: leadScore?.demographicScore || 0, color: '#8B5CF6' },
    { name: 'Engagement', value: leadScore?.engagementScore || 0, color: '#EC4899' },
    { name: 'Activity', value: leadScore?.activityScore || 0, color: '#F97316' },
    { name: 'Recency', value: leadScore?.recencyScore || 0, color: '#10B981' }
  ];

  // Loading state for history chart
  const HistoryLoadingState = () => (
    <div className="flex flex-col space-y-3 py-4">
      <Skeleton className="h-[300px] w-full rounded-md" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Lead Scoring</h2>
          <p className="text-muted-foreground">
            Comprehensive analysis of {lead.name}'s engagement and qualification metrics
          </p>
        </div>
        <Button 
          onClick={() => recalculateScore(lead)} 
          disabled={isCalculating}
        >
          {isCalculating ? 'Calculating...' : 'Recalculate Score'}
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Current Score</CardTitle>
                <CardDescription>
                  Total score: {leadScore?.totalScore || 'Not calculated'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={scoreComponents}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 50]} />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name="Score" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Score Factors</CardTitle>
                <CardDescription>
                  What contributes to this lead's score
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="space-y-4">
                  {leadScore ? (
                    <>
                      <div>
                        <h3 className="text-sm font-medium mb-1">Demographic Factors</h3>
                        <p className="text-sm text-muted-foreground">
                          Business details, contact information, and key contacts
                        </p>
                        <div className="h-2 w-full bg-slate-100 rounded-full mt-1">
                          <div 
                            className="h-2 bg-purple-500 rounded-full" 
                            style={{ width: `${(leadScore.demographicScore / 50) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium mb-1">Engagement Factors</h3>
                        <p className="text-sm text-muted-foreground">
                          Lead status progression and interaction level
                        </p>
                        <div className="h-2 w-full bg-slate-100 rounded-full mt-1">
                          <div 
                            className="h-2 bg-pink-500 rounded-full" 
                            style={{ width: `${(leadScore.engagementScore / 60) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium mb-1">Activity Factors</h3>
                        <p className="text-sm text-muted-foreground">
                          Notes, documents, and recorded interactions
                        </p>
                        <div className="h-2 w-full bg-slate-100 rounded-full mt-1">
                          <div 
                            className="h-2 bg-orange-500 rounded-full" 
                            style={{ width: `${(leadScore.activityScore / 50) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium mb-1">Recency Factors</h3>
                        <p className="text-sm text-muted-foreground">
                          How recently you've engaged with this lead
                        </p>
                        <div className="h-2 w-full bg-slate-100 rounded-full mt-1">
                          <div 
                            className="h-2 bg-emerald-500 rounded-full" 
                            style={{ width: `${(leadScore.recencyScore / 30) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground">
                        No score data available. Click "Recalculate Score" to generate a new score.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Score Interpretation</CardTitle>
              <CardDescription>Understanding what this score means</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="font-medium text-blue-700 dark:text-blue-300">Cold (0-39)</h3>
                    <p className="text-sm mt-1">New leads with minimal information or engagement</p>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <h3 className="font-medium text-yellow-700 dark:text-yellow-300">Warm (40-69)</h3>
                    <p className="text-sm mt-1">Leads showing interest with some engagement history</p>
                  </div>
                  
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <h3 className="font-medium text-orange-700 dark:text-orange-300">Hot (70-89)</h3>
                    <p className="text-sm mt-1">Qualified leads with strong engagement indicators</p>
                  </div>
                  
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <h3 className="font-medium text-red-700 dark:text-red-300">Very Hot (90-100)</h3>
                    <p className="text-sm mt-1">Highly qualified leads nearing conversion</p>
                  </div>
                </div>
                
                <div className="mt-4 text-sm">
                  <p className="mb-2">
                    <strong>What affects this score:</strong>
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Completeness of lead information (contact details, key contacts)</li>
                    <li>Lead status progression through your sales pipeline</li>
                    <li>Number and quality of interactions (notes, documents, calls, emails)</li>
                    <li>How recently you've interacted with this lead</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="details">
          <LeadScoreBreakdown 
            scoreBreakdown={scoreBreakdown}
            isLoading={isLoading}
          />
        </TabsContent>
        
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Score History</CardTitle>
              <CardDescription>How this lead's score has changed over time</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <HistoryLoadingState />
              ) : scoreHistory.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">
                    No score history available yet. Recalculate the score to begin tracking changes.
                  </p>
                  <Button 
                    onClick={recalculateScore} 
                    disabled={isCalculating}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                  >
                    Recalculate Score
                  </Button>
                </div>
              ) : (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={scoreHistory}
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="demographic" name="Demographic" stackId="a" fill="#8B5CF6" />
                      <Bar dataKey="engagement" name="Engagement" stackId="a" fill="#EC4899" />
                      <Bar dataKey="activity" name="Activity" stackId="a" fill="#F97316" />
                      <Bar dataKey="recency" name="Recency" stackId="a" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ScoreTabContent;
